import { defineStore } from "pinia";
import { Group, GroupForm, GroupRulesForm, GroupSettingsForm } from "@/common/models/group";
import { Player } from "@/common/models/player";
import {
  createNewPlayer,
  editInfoGroup,
  editRulesGroup,
  editSettingsGroup,
  exitGroup,
  fetchMeGroups,
  fetchGroupById,
  fetchInvitationTokenGroup,
  joinGroup,
  declinePlayer,
  doPlayerAdmin,
} from "@/common/services/groups.service";
import { removeEntity, upsertEntities, replaceCollection } from "@/common/store/utils";
import { usePlayersStore } from "@/common/store/players.store";
import { useErrorStore } from "@/common/store/error.store";
import { useCompetitionsStore } from "@/common/store/competitions.store";
import { useBetsStore } from "@/common/store/bets.store";
import { User } from "@/common/models/user";

interface GroupsState {
  ids: string[];
  entities: { [key: string]: Group };
}

export const useGroupsStore = defineStore("groups", {
  state: (): GroupsState => ({
    ids: [],
    entities: {},
  }),

  getters: {
    all(): Group[] {
      const playersStore = usePlayersStore();
      const competitionStore = useCompetitionsStore();
      const entities = this.ids
        .map((id) => this.entities[id])
        .map((group) => {
          const competition = competitionStore.all.find((c) => c.id === group.competition);
          return {
            ...group,
            competition: competition ? competition : null,
            players: playersStore.all.filter((p) => p.group === group.id),
          };
        });
      return entities;
    },
  },

  actions: {
    async fetchGroups(): Promise<void> {
      try {
        const groups = await fetchMeGroups();
        this.fetchGroupsSuccess(groups);
      } catch (error) {
        this.fetchGroupsError(error);
      }
    },

    fetchGroupsSuccess(groups: Group[]): void {
      replaceCollection(groups, this);
    },

    fetchGroupsError(err: any): void {
      const errorStore = useErrorStore();
      errorStore.throwError(`fetchUserError: ${err.toString()}`);
    },

    async fetchGroup(id: string): Promise<void> {
      const playersStore = usePlayersStore();
      const betsStore = useBetsStore();
      try {
        const group = await fetchGroupById(id);
        await playersStore.fetchGroupPlayers(group);
        await betsStore.fetchPlayerMeBets(group);
        this.fetchGroupSuccess(group);
      } catch (error) {
        this.fetchGroupError(error);
      }
    },

    fetchGroupSuccess(group: Group): void {
      upsertEntities([group], this);
    },

    fetchGroupError(err: any): void {
      const errorStore = useErrorStore();
      errorStore.throwError(`fetchGroupError: ${err.toString()}`);
    },

    async fetchInvitationTokenGroup(id: string): Promise<string> {
      return fetchInvitationTokenGroup(id);
    },

    async createNewPlayerAction(users: User[], group: Group): Promise<void> {
      await createNewPlayer(users, group);
      await usePlayersStore().fetchGroupPlayers(group);
    },

    async declineUserAction(group: Group, player: Player) {
      try {
        await declinePlayer(group, player);
        await this.declineUserActionSuccess(group, player);
      } catch (error) {
        this.declineUserActionError(error);
      }
    },

    async declineUserActionSuccess(group: Group, player: Player) {
      const playerStore = usePlayersStore();
      playerStore.removePlayer(player);
      await playerStore.fetchGroupPlayers(group);
    },

    declineUserActionError(err: any) {
      const errorStore = useErrorStore();
      errorStore.throwError(`declineUserActionError: ${err.toString()}`);
      throw new Error("cannot decline user");
    },

    async doPlayerAdmin(group: Group, player: Player) {
      try {
        const updatedPlayer = await doPlayerAdmin(group, player);
        this.doPlayerAdminSuccess(updatedPlayer);
      } catch (error) {
        this.doPlayerAdminError(error);
      }
    },
    doPlayerAdminSuccess(player: Player) {
      usePlayersStore().upsertPlayer(player);
    },
    doPlayerAdminError(err: any) {
      const errorStore = useErrorStore();
      errorStore.throwError(`doPlayerAdminError: ${err.toString()}`);
      throw new Error("cannot do player admin");
    },

    async exitGroup(group: Group): Promise<void> {
      try {
        await exitGroup(group);
        this.exitGroupSuccess(group);
      } catch (error) {
        this.exitGroupError(error);
      }
    },

    exitGroupSuccess(group: Group): void {
      removeEntity(group, this);
    },

    exitGroupError(err: any): void {
      const errorStore = useErrorStore();
      errorStore.throwError(`exitGroupError: ${err.toString()}`);
    },

    async editInfoGroup(group: GroupForm, groupId: string): Promise<void> {
      await editInfoGroup(group, groupId);
    },

    async editSettingsGroup(group: GroupSettingsForm, groupId: string): Promise<void> {
      await editSettingsGroup(group, groupId);
    },

    async editRulesGroup(groupRules: GroupRulesForm[], groupId: string): Promise<void> {
      await editRulesGroup(groupRules, groupId);
    },

    async joinGroup(tokenInvitation: string): Promise<void> {
      await joinGroup(tokenInvitation);
    },
  },
});
